/**
 * Copy for /financing-solar-lease-plan (Hometree Finance Solar Plan).
 */

export const SOLAR_LEASE_PLAN_PATH = "/financing-solar-lease-plan" as const;

export const SOLAR_LEASE_PLAN_META = {
  title: "The Solar Plan: Solar Lease Finance | CRS Energy",
  description:
    "The Solar Plan from Hometree Finance is a lease rather than a loan, with monitoring, maintenance and repairs included for the life of the plan.",
} as const;

export const SOLAR_LEASE_HERO = {
  eyebrow: "HOME ENERGY LEASE, PROVIDED BY HOMETREE FINANCE",
  h1: "The Solar Plan: A Different Way To Pay For Solar",
  body: "Alongside our loan option with Ideal4Finance, solar and battery customers can also spread the cost through Hometree Finance's Solar Plan - a lease rather than a loan, with maintenance and repairs included for the life of the plan.",
  smallPrint:
    "Available for solar PV, plus optional battery storage, diverter or EV charger, on homes in England and Wales. Subject to status and a hire agreement.",
} as const;

export const SOLAR_LEASE_HIGHLIGHTS: { value: string; label: string }[] = [
  { value: "5-25 years", label: "Plan Terms Available" },
  { value: "£0", label: "Upfront-Cost Options Available" },
  { value: "0", label: "Callout Fees For The Life Of The Plan" },
  { value: "14 days", label: "Cooling-Off Period" },
];

export const SOLAR_LEASE_PARTNER_BANNER =
  "We've partnered with Hometree Finance, who provide the Solar Plan lease. CRS Energy does not provide or recommend finance";

export const SOLAR_LEASE_JOURNEY_STEPS = [
  {
    title: "Get Your Solar Design & Quote",
    body: "We design your system and give you a written CRS Energy quote, exactly as we would for any solar installation.",
  },
  {
    title: "We Set Up Your Solar Plan Quote",
    body: "At your request, our team enters your quote details into Hometree Finance's portal so they can produce your personalised Solar Plan quote, and sends you a secure link.",
  },
  {
    title: "Apply Directly With Hometree Finance",
    body: "You complete the application yourself, directly with Hometree Finance. It's a secure online process that typically takes a couple of minutes, and in most cases you'll get an instant decision.",
  },
  {
    title: "Choose Your Term & Sign",
    body: "If approved, you choose your plan term - from 5 to 25 years - review the agreement online, and set up your Direct Debit.",
  },
  {
    title: "Cooling-Off Period",
    body: "As with any regulated agreement, you have a 14-day cooling-off period after signing.",
  },
  {
    title: "We Schedule Your Installation",
    body: "Once your agreement is in place, Hometree Finance issues the purchase order and we book your installation.",
  },
  {
    title: "Installation",
    body: "Our accredited team installs your system as designed.",
  },
  {
    title: "Payments Begin After Commissioning",
    body: "Your monthly payments to Hometree Finance only start once your system is installed and commissioned - not before.",
  },
  {
    title: "Ongoing Cover",
    body: "For the life of the plan, Hometree Finance covers system monitoring, maintenance and repairs, with no callout fees. Their support team is your first point of contact for anything plan-related once you're live.",
  },
] as const;

export type LeaseCardItem = {
  title: string;
  description: string;
};

export const SOLAR_LEASE_WHAT_YOU_GET = [
  {
    title: "Low Or No Upfront Cost",
    description: "£0 or low upfront-cost options available.",
  },
  {
    title: "No Payment Until Installed",
    description:
      "No payment until your system is installed and generating.",
  },
  {
    title: "Full Maintenance & Repairs",
    description:
      "Monitoring, maintenance and unlimited repairs for the life of the plan, with no callout fees.",
  },
  {
    title: "Replacement Protection",
    description:
      "Replacement protection for parts that fail and can't be repaired, even after the manufacturer warranty ends.",
  },
  {
    title: "Keep Your Savings",
    description:
      "You keep all the electricity savings and Smart Export Guarantee payments generated by your system.",
  },
  {
    title: "Settle Any Time",
    description:
      "Settle the plan at any time by paying the settlement figure in your agreement, with no penalty.",
  },
  {
    title: "Ownership At Plan End",
    description:
      "At the end of the plan, Hometree Finance's policy is to offer to transfer ownership of the equipment to you at no extra cost.",
  },
] as const;

export const SOLAR_LEASE_THINGS_TO_KNOW = [
  {
    title: "Lease, Not A Loan",
    description:
      "This is a lease, not a loan. Hometree Finance owns the equipment during the plan term - you don't.",
  },
  {
    title: "2.5% Annual Increase",
    description:
      "Monthly payments increase by a fixed 2.5% each year, to help cover the cost of maintaining your system over time.",
  },
  {
    title: "Restriction On Title",
    description:
      "Hometree Finance places a restriction on your property's title after installation. This is not a charge - it gives Hometree Finance no rights over your property and no power to force a sale. It simply means they're notified if you sell, and it's removed automatically once the plan ends, provided payments are up to date.",
  },
  {
    title: "Missed Payments",
    description:
      "Missing payments could have serious consequences and make it harder to obtain credit in the future.",
  },
  {
    title: "All Owners Included",
    description:
      "All legal owners of the property must be included in the agreement.",
  },
  {
    title: "Smart Meter Required",
    description:
      "You'll need a smart meter - or agree to have one fitted alongside your installation - to receive Smart Export Guarantee payments.",
  },
] as const;

export const SOLAR_LEASE_ELIGIBILITY_INTRO =
  "These are Hometree Finance's own published product parameters, shown here for information. Whether a plan is offered, and on what terms, is decided by Hometree Finance based on your application - CRS Energy doesn't make that decision.";

export const SOLAR_LEASE_ELIGIBILITY = [
  "Homeowners - owner-occupiers or private landlords - in England or Wales",
  "Aged 18 or over, and no older than 84 at the end of your chosen plan term (so the maximum age to apply for a 25-year term is 59)",
  "Typical order values range from £6,000 to £50,000",
  "Application is subject to status",
] as const;

export const SOLAR_LEASE_FAQS = [
  {
    q: "Do I own my solar system during the plan?",
    a: "No. Hometree Finance owns the equipment for the life of the plan and is responsible for maintaining it. You keep the electricity savings, generation, and Smart Export Guarantee payments.",
  },
  {
    q: "What happens at the end of the plan?",
    a: "Hometree Finance's policy is to offer to transfer ownership of the equipment to you at no extra cost. You can also choose to hand the system back, or in some cases continue on new terms - Hometree Finance will confirm your options as your plan term approaches its end.",
  },
  {
    q: "Can I settle the plan early?",
    a: "Yes. You can end the plan at any time by paying the settlement amount shown in your agreement, with no penalty.",
  },
  {
    q: "What if I sell my house during the plan?",
    a: "Hometree Finance places a restriction on your property's title, not a charge, so they're notified if you sell. It gives them no right to block the sale or force repayment - ownership of the Solar Plan can be transferred as part of the sale, subject to Hometree Finance's process.",
  },
  {
    q: "Will applying affect my credit score?",
    a: "Hometree Finance's application process is designed to be quick, and in most cases gives an instant decision. For the exact detail on how the application affects your credit file, this is best confirmed directly with Hometree Finance during your application.",
  },
  {
    q: "Is this the same as a loan?",
    a: "No. The Solar Plan is a lease - a regulated consumer hire agreement - not a loan. Hometree Finance owns the equipment during the plan term, and the payment doesn't carry an APR the way a loan would.",
  },
] as const;

export const SOLAR_LEASE_DISCLOSURE = [
  "Complete Remedial Solutions (CRS) Ltd, trading as CRS Energy, is an Introducer Appointed Representative of HTG Finance Limited (FRN 825534) which is authorised and regulated by the Financial Conduct Authority.",
  "The Solar Plan is a regulated consumer hire agreement, not a loan, and does not have an APR.",
  "Price varies, apply for your personal quote. Offers are subject to status and finance agreement. Monthly payments are subject to an annual increase of 2.5%. Missing payments could have severe consequences and make obtaining credit more difficult.",
] as const;
